// =========================================================================
// COSTS, phase-pivoted view only
// =========================================================================
// =========================================================================
// INTERACTIVE SAMPLE-SIZE SANDBOX
// Clients can edit the quoted "Sample (n)" cells and watch costs rescale
// live, purely in the browser, nothing is saved. Every amount row
// carries a backend-computed `scale_with` tag: a block id (scales with
// that audience/methodology block's sample), "phase" (scales with the
// phase-wide per-country sample) or nothing (fixed cost, translations,
// setup/admin, study management).
// =========================================================================
export const r2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;

// Incentive-row classifier shared with the renderer / the ceil5 pass:
// a per-respondent participant payout (no caption_rate, "Incentive" in
// the label, e.g. "Incentives" or "Incentives task"). Handling-fee
// rows carry a caption_rate so they're filtered out here, they ride
// on TOP of the incentive amount via a separate row.
export const isIncentiveAmountRow = (row) =>
  row?.kind === "amount" &&
  row.caption_rate == null &&
  /incentive/i.test(row.label || "");

// Round-up-to-nearest-5 in target-currency space:
// ``ceil5(108.90) === 110``, ``ceil5(61.25) === 65``,
// ``ceil5(110) === 110`` (already clean).
export const ceil5 = (n) => Math.ceil(Number(n) / 5) * 5;

// =========================================================================
// DISPLAY-CURRENCY ROUNDING (round to nearest 5 in target currency)
// =========================================================================
// When the proposal is displayed in any currency other than GBP,
// every per-unit converts at the pure mid-market rate and is then
// rounded to the nearest 5 in the target currency. The direction
// depends on the cost type:
//
//   • Incentive rows  → round DOWN (floor5). Participants are
//     paid the on-the-ground local currency amount; rounding down
//     keeps the foreign payout slightly favourable to Medicys and
//     avoids over-quoting the client for a cost that's already a
//     headline number on the proposal.
//   • Everything else → round UP (ceil5). Recruitment, project
//     management, services, handling fees - these are Medicys'
//     own costs, rounding up absorbs the cents into a clean
//     headline number without ever under-quoting.
//
// The cell becomes `snapped_pu × units` (a multiple of 5 because
// units is integer), so the caption ("$135 × 2") and the cell
// ("$270") line up byte-for-byte. Handling-fee rows
// (caption_rate × base) ceil5 their cell amount the same way.
// Phase subtotals + grand total are sums of multiples of 5, also
// multiples of 5.
//
// Pass over every amount row with both `per_unit[c]` and
// `units[c]` (incentives, recruitment, project management,
// service/setup units), snap the per-unit per the directional
// rule, store its GBP-equivalent, then re-derive `by_country[c]`
// as `snapped_pu × units`. The paired handling-fee row's
// caption_base + by_country are recomputed against the reconciled
// incentive sums and the cell amount is ceil5'd in target.
//
// Lump-sum rows (no per_unit/units), project-wide globals and
// translation rows are left untouched. Translations are quoted
// per-word and bill thousands of units; a nearest-5 snap on a
// €0.12/word per-unit would distort the headline by tens of
// percent. GBP-displayed proposals are a no-op (the canonical
// quote is already in pounds, no conversion). Function name is
// retained for callsite stability; the original GBP per_unit is
// stashed on `_quoted_per_unit` so the local-payout sub-caption
// can still surface the un-rounded native amount (which the
// renderer floor5s in the country's native currency for non-GBP-
// native markets, matching the incentive rule).
// =========================================================================
const floor5 = (n) => Math.floor(Number(n) / 5) * 5;
export function applyIncentiveCeil5(phase, targetCurrency, targetRate, _countryFx) {
  if (!phase || !targetCurrency || targetCurrency === "GBP") return phase;
  const rate = Number(targetRate) || 0;
  if (!rate || rate === 1) return phase;
  const countries = phase.countries || [];
  if (!countries.length) return phase;

  // Pass 1: snap per_unit / by_country / total for every amount row
  // that has both per_unit[c] and units[c] (incentives, recruitment,
  // project management, service/setup units). Lump-sum rows
  // (no per_unit) and project-wide globals are skipped. Translation
  // rows are also skipped - their per-unit is a per-word cost and
  // their units are large (thousands of words), nearest-5 rounding
  // would dwarf the unit cost and distort the headline figure (a
  // per-word cost of €0.12 doesn't survive ceil5 to a multiple of
  // 5, and the cell would be off by tens of percent).
  const isTranslationRow = (row) =>
    /translat|localis|localiz|loclis/i.test(row?.label || "");
  const incentiveSumByCountry = Object.fromEntries(countries.map((c) => [c, 0]));
  let touched = false;
  const newSections = phase.sections.map((sec) => ({
    ...sec,
    rows: sec.rows.map((row) => {
      if (row.kind !== "amount") return row;
      if (row._global) return row;
      // Handling-fee rows (caption_rate, no per_unit/units) are
      // re-derived in pass 2 against the snapped incentive sums.
      if (row.caption_rate != null) return row;
      // Translation rows render at the exact mid-rate conversion.
      if (isTranslationRow(row)) return row;
      const isIncentive = isIncentiveAmountRow(row);
      const newPerUnit = { ...(row.per_unit || {}) };
      const newByCountry = { ...(row.by_country || {}) };
      let newTotal = 0;
      let rowTouched = false;
      countries.forEach((c) => {
        const pu = row.per_unit?.[c];
        const u = row.units?.[c];
        if (!pu || !u) {
          // Lump-sum cell, keep as-is.
          if (row.by_country?.[c]) {
            newByCountry[c] = row.by_country[c];
            newTotal += row.by_country[c];
            if (isIncentive) incentiveSumByCountry[c] += row.by_country[c];
          }
          return;
        }
        // Snap per_unit to the nearest 5 in target currency. Direction
        // depends on the cost type: incentives floor5 (round DOWN,
        // participant payout), everything else ceil5 (round UP,
        // Medicys' own cost). Store the GBP-equivalent so the
        // existing `money() = pu × rate` pipeline reproduces the
        // snapped amount exactly. The cell becomes
        // `snapped_pu × units` (in GBP-equivalent) so caption ×
        // units = cell, byte-for-byte.
        const rawPuTarget = Number(pu) * rate;
        const origCell = Number(row.by_country?.[c]) || 0;
        if (
          rawPuTarget < 5 ||
          (origCell && Math.abs(origCell - Number(pu) * Number(u)) > 0.02)
        ) {
          // Two cases keep the exact mid-rate conversion:
          // 1. A per-unit below 5 in the target currency cannot survive
          //    a nearest-5 snap: ceil5 would multiply a per-word cost
          //    (for example GBP 0.22/word x 12,000 words) many times over
          //    and floor5 would zero a small payout.
          // 2. The quoted cell is NOT per_unit x units (hidden multiplier
          //    in the source data, for example transcript rows billed at
          //    3x the shown per-unit), rebuilding as snapped_pu x units
          //    would silently rewrite the quoted amount.
          if (origCell) {
            newByCountry[c] = origCell;
            newTotal += origCell;
            if (isIncentive) incentiveSumByCountry[c] += origCell;
          }
          return;
        }
        const snap = isIncentive ? floor5 : ceil5;
        const displayedPuTarget = snap(rawPuTarget);
        const newPuGBP = displayedPuTarget / rate;
        if (Math.abs(newPuGBP - Number(pu)) > 1e-9) rowTouched = true;
        newPerUnit[c] = newPuGBP;
        newByCountry[c] = newPuGBP * Number(u);
        newTotal += newByCountry[c];
        if (isIncentive) incentiveSumByCountry[c] += newByCountry[c];
      });
      if (!rowTouched) {
        if (isIncentive) {
          // No drift to snap, but record the incentive cells so the
          // handling-fee pass still finds the base.
          countries.forEach((c) => {
            const v = row.by_country?.[c];
            if (v) incentiveSumByCountry[c] += v;
          });
        }
        return row;
      }
      touched = true;
      return {
        ...row,
        _quoted_per_unit: row.per_unit,
        per_unit: newPerUnit,
        by_country: newByCountry,
        total: newTotal,
      };
    }),
  }));

  if (!touched) return phase;

  // Pass 2: re-derive every handling-fee row's caption_base + by_country.
  // - Paired ("_handling_fee_pair: true"): caption_base is the
  //   PRECEDING incentive row's new by_country.
  // - Phase-wide (caption_rate != null but not paired): caption_base
  //   is the running phase-wide incentive sum.
  const sectionsWithFees = newSections.map((sec) => {
    const outRows = [];
    let lastIncentiveBy = null;
    sec.rows.forEach((row) => {
      if (isIncentiveAmountRow(row)) {
        lastIncentiveBy = row.by_country;
        outRows.push(row);
        return;
      }
      if (row.kind !== "amount" || row.caption_rate == null) {
        outRows.push(row);
        return;
      }
      const baseSource =
        row._handling_fee_pair && lastIncentiveBy
          ? lastIncentiveBy
          : incentiveSumByCountry;
      const newCaptionBase = {};
      const newByCountry = {};
      let newTotal = 0;
      let baseSum = 0;
      countries.forEach((c) => {
        const base = Number(baseSource[c]) || 0;
        if (!base) {
          newCaptionBase[c] = null;
          newByCountry[c] = null;
          return;
        }
        const fee = Number(row.caption_rate) * base;
        newCaptionBase[c] = base;
        baseSum += base;
        if (fee > 0) {
          // Ceil5 the fee in target currency, then store its
          // GBP-equivalent so `money(fee) = fee × rate` displays
          // as the rounded multiple of 5.
          const feeTargetCeil5 = ceil5(fee * rate);
          const feeGBP = feeTargetCeil5 / rate;
          newByCountry[c] = feeGBP;
          newTotal += feeGBP;
        } else {
          newByCountry[c] = null;
        }
      });
      outRows.push({
        ...row,
        caption_base: newCaptionBase,
        caption_base_total: baseSum,
        by_country: newByCountry,
        total: newTotal,
      });
    });
    return { ...sec, rows: outRows };
  });

  // Pass 3: re-sum country subtotals + grand total over every amount
  // row that's NOT _global (globals are project-wide fees allocated
  // only to the Total column).
  const subtotals = Object.fromEntries(countries.map((c) => [c, 0]));
  let grand = 0;
  for (const sec of sectionsWithFees) {
    for (const row of sec.rows) {
      if (row.kind !== "amount") continue;
      if (row._global) {
        grand += row.total || 0;
        continue;
      }
      let rowTotal = 0;
      countries.forEach((c) => {
        const v = row.by_country?.[c];
        if (v) {
          subtotals[c] += v;
          rowTotal += v;
        }
      });
      grand += rowTotal;
    }
  }

  return {
    ...phase,
    sections: sectionsWithFees,
    subtotal_by_country: Object.fromEntries(
      countries.map((c) => [c, subtotals[c]])
    ),
    grand_total: grand,
  };
}

// Parse a raw unit-override input: a finite number ≥ 0, else null.
const parseUnits = (raw) => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * Display-space reconciliation for FX viewing. Every converted cell
 * the client sees is rounded to 2 dp - so subtotals and grand totals
 * must be the sum of those ROUNDED cell values, not a one-shot
 * conversion of the GBP sums, or the visible column won't add up and
 * the Excel export (which sums its own 2 dp cells) will disagree by
 * pennies. Values stay stored as GBP-equivalents (displayed × rate).
 * GBP display is a no-op.
 */
export function reconcileDisplayTotals(phase, targetCurrency, targetRate) {
  const rate = Number(targetRate) || 0;
  if (!phase || !targetCurrency || targetCurrency === "GBP" || !rate || rate === 1) {
    return phase;
  }
  const countries = phase.countries || [];
  const disp = (v) => r2(v * rate) / rate; // snap to a whole displayed penny
  const subtotals = Object.fromEntries(countries.map((c) => [c, 0]));
  let grand = 0;
  const sections = (phase.sections || []).map((sec) => ({
    ...sec,
    rows: (sec.rows || []).map((row) => {
      if (row.kind !== "amount") return row;
      if (row._global) {
        const t = row.total ? disp(row.total) : row.total;
        grand += t || 0;
        return t === row.total ? row : { ...row, total: t };
      }
      const by = { ...(row.by_country || {}) };
      let total = 0;
      countries.forEach((c) => {
        if (!by[c]) return;
        by[c] = disp(by[c]);
        subtotals[c] += by[c];
        total += by[c];
      });
      grand += total;
      return { ...row, by_country: by, total };
    }),
  }));
  return {
    ...phase,
    sections,
    subtotal_by_country: subtotals,
    grand_total: grand,
  };
}

export function adjustPivot(phase, phaseIndex, overrides, unitOverrides) {
  const countries = phase.countries || [];
  const sections = phase.sections || [];

  // Quoted samples per block (from the count rows).
  const quotedSamples = {};
  sections.forEach((sec) =>
    sec.rows.forEach((row) => {
      if (row.kind === "count" && row.block_id) {
        quotedSamples[row.block_id] = row.by_country || {};
      }
    })
  );

  // Effective samples + per-block scale factors.
  const newSamples = {};
  const blockFactor = {};
  let anyChanged = false;
  let anyIncrease = false;
  Object.entries(quotedSamples).forEach(([bid, samples]) => {
    newSamples[bid] = {};
    blockFactor[bid] = {};
    countries.forEach((c) => {
      const q = Number(samples[c]) || 0;
      const raw = overrides[`${phaseIndex}:${bid}:${c}`];
      let n = q;
      if (q > 0 && raw != null && raw !== "") {
        const parsed = Math.round(Number(raw));
        if (Number.isFinite(parsed) && parsed >= 0) n = parsed;
      }
      newSamples[bid][c] = n;
      blockFactor[bid][c] = q > 0 ? n / q : 1;
      if (q > 0 && n !== q) anyChanged = true;
      if (n > q) anyIncrease = true;
    });
  });

  // Direct unit edits on service/setup rows (rows without a block_id).
  // An override pins that cell to per_unit × units regardless of any
  // sample-driven scaling.
  let unitChanged = false;
  let unitIncrease = false;
  sections.forEach((sec, si) =>
    sec.rows.forEach((row, ri) => {
      if (row.kind !== "amount" || row.block_id) return;
      const prefix = `${phaseIndex}:s${si}:r${ri}:`;
      if (row._global) {
        if (row.global_per_unit && row.global_units) {
          const parsed = parseUnits(unitOverrides[prefix + "total"]);
          if (parsed != null && parsed !== Number(row.global_units)) {
            unitChanged = true;
            if (parsed > Number(row.global_units)) unitIncrease = true;
          }
        }
        return;
      }
      countries.forEach((c) => {
        const q = row.units?.[c];
        if (!q || !row.per_unit?.[c]) return;
        const parsed = parseUnits(unitOverrides[prefix + c]);
        if (parsed != null && parsed !== Number(q)) {
          unitChanged = true;
          if (parsed > Number(q)) unitIncrease = true;
        }
      });
    })
  );

  if (!anyChanged && !unitChanged) {
    return { phase, anyChanged: false, anyIncrease: false };
  }
  anyChanged = true;
  anyIncrease = anyIncrease || unitIncrease;

  // Per-country phase factors (for "phase"-scaled rows) and the
  // overall factor (for phase-wide Total-only rows).
  const phaseFactor = {};
  let quotedAll = 0;
  let newAll = 0;
  countries.forEach((c) => {
    let q = 0;
    let n = 0;
    Object.keys(quotedSamples).forEach((bid) => {
      q += Number(quotedSamples[bid][c]) || 0;
      n += newSamples[bid][c] || 0;
    });
    phaseFactor[c] = q > 0 ? n / q : 1;
    quotedAll += q;
    newAll += n;
  });
  const overallFactor = quotedAll > 0 ? newAll / quotedAll : 1;

  const factorFor = (row, c) => {
    if (!row.scale_with) return 1;
    if (row.scale_with === "phase") return phaseFactor[c] ?? 1;
    return blockFactor[row.scale_with]?.[c] ?? 1;
  };

  // Rebuild rows with scaled amounts, units captions and fee bases.
  const subtotals = {};
  countries.forEach((c) => (subtotals[c] = 0));
  let grand = 0;

  const adjSections = sections.map((sec, si) => ({
    ...sec,
    rows: sec.rows.map((row, ri) => {
      if (row.kind === "count" && row.block_id) {
        const by = {};
        countries.forEach((c) => (by[c] = newSamples[row.block_id][c]));
        return {
          ...row,
          quoted_by_country: row.by_country,
          by_country: by,
          total: countries.reduce((a, c) => a + (by[c] || 0), 0),
        };
      }
      // PRF forms follow the edited respondent count at the quoted
      // ratio (60 oncologists x 8 = 480 forms).
      if (row.kind === "count" && row.derived_from && newSamples[row.derived_from]) {
        const by = {};
        countries.forEach((c) => {
          const per = Number(row.derived_units_per?.[c]);
          by[c] = per > 0 ? Math.round(newSamples[row.derived_from][c] * per) : row.by_country?.[c];
        });
        return {
          ...row,
          quoted_by_country: row.by_country,
          by_country: by,
          total: countries.reduce((a, c) => a + (by[c] || 0), 0),
        };
      }
      if (row.kind !== "amount") return row;
      if (row._global) {
        const o =
          !row.block_id && row.global_per_unit
            ? parseUnits(unitOverrides[`${phaseIndex}:s${si}:r${ri}:total`])
            : null;
        if (o != null) {
          const total = r2(row.global_per_unit * o);
          grand += total;
          return {
            ...row,
            total,
            global_units: o,
            quoted_global_units: row.global_units,
          };
        }
        const total = r2((row.total || 0) * (row.scale_with ? overallFactor : 1));
        grand += total;
        return total === row.total ? row : { ...row, total };
      }
      const by = {};
      const units = {};
      // Sample-scaled units per country, ignoring any direct unit
      // override. This is what the cell falls back to when an
      // override is cleared, so the blur handler can tell "typed the
      // baseline back" (clear the pin) apart from "typed the quoted
      // value while sample scaling is active" (keep the pin).
      const baselineUnits = !row.block_id ? {} : null;
      const base = row.caption_base ? {} : null;
      let total = 0;
      countries.forEach((c) => {
        const pu = row.per_unit?.[c];
        const f = factorFor(row, c);
        const u = row.units?.[c];
        const scaledU = u ? Math.round(u * f) : u;
        if (baselineUnits) baselineUnits[c] = scaledU;
        const o = !row.block_id
          ? parseUnits(unitOverrides[`${phaseIndex}:s${si}:r${ri}:${c}`])
          : null;
        if (o != null && pu) {
          // Direct unit edit pins this cell.
          units[c] = o;
          by[c] = r2(pu * o);
          if (by[c]) {
            subtotals[c] += by[c];
            total += by[c];
          }
          if (base) base[c] = row.caption_base[c];
          return;
        }
        const v = row.by_country?.[c];
        by[c] = v ? r2(v * f) : v;
        if (by[c]) {
          subtotals[c] += by[c];
          total += by[c];
        }
        units[c] = scaledU;
        if (base) {
          const b = row.caption_base[c];
          base[c] = b ? r2(b * f) : b;
        }
      });
      total = r2(total);
      grand += total;
      const out = { ...row, by_country: by, units, total };
      if (!row.block_id) {
        out.quoted_units = row.units;
        out.baseline_units = baselineUnits;
      }
      if (base) {
        out.caption_base = base;
        out.caption_base_total = r2(
          countries.reduce((a, c) => a + (base[c] || 0), 0)
        );
      }
      return out;
    }),
  }));

  return {
    phase: {
      ...phase,
      sections: adjSections,
      subtotal_by_country: Object.fromEntries(
        countries.map((c) => [c, r2(subtotals[c])])
      ),
      grand_total: r2(grand),
    },
    anyChanged: true,
    anyIncrease,
  };
}
